import React, { useMemo, useState } from "react";
import { Box, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { ArrowRight, Folder } from "@mui/icons-material";
import { ProcessModelFolderType } from "model/processModel";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface MoveToFolderMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  folders: ProcessModelFolderType[];
  excludeIds: Set<number>;
  currentParentId: number | null;
  onPick: (targetFolderId: number | null) => void;
}

interface FolderNodeProps {
  folder: ProcessModelFolderType;
  depth: number;
  excludeIds: Set<number>;
  currentParentId: number | null;
  onPick: (targetFolderId: number | null) => void;
  onClose: () => void;
}

const collectDescendantIds = (
  folder: ProcessModelFolderType,
  out: Set<number>
): void => {
  for (const child of folder.children || []) {
    out.add(child.id);
    collectDescendantIds(child, out);
  }
};

const FolderNode: React.FC<FolderNodeProps> = ({
  folder,
  depth,
  excludeIds,
  currentParentId,
  onPick,
  onClose,
}) => {
  const translations = useTranslations();
  const [submenuAnchor, setSubmenuAnchor] = useState<HTMLElement | null>(null);
  const disabled = excludeIds.has(folder.id);
  const isCurrent = currentParentId === folder.id;
  const open = Boolean(submenuAnchor);
  const hasChildren = (folder.children || []).length > 0;

  const handlePick = (event: React.MouseEvent) => {
    if (disabled) return;
    event.stopPropagation();
    onPick(folder.id);
    onClose();
  };

  const handleOpenSubmenu = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled || !hasChildren) return;
    event.stopPropagation();
    setSubmenuAnchor(event.currentTarget);
  };

  const handlePickFromSubmenu = (target: number | null) => {
    onPick(target);
    setSubmenuAnchor(null);
    onClose();
  };

  return (
    <React.Fragment>
      <MenuItem
        disabled={disabled}
        onClick={handlePick}
        sx={{ pl: 1 + depth * 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
          <Folder fontSize="small" sx={{ color: disabled ? "text.disabled" : "primary.main" }} />
          <Typography
            variant="body2"
            sx={{ flex: 1, opacity: isCurrent ? 0.6 : 1 }}
          >
            {folder.name}
            {isCurrent ? ` (${getTranslation(translations, "process_library.current")})` : ""}
          </Typography>
          {hasChildren && !disabled && (
            <Box
              role="button"
              aria-label={`${getTranslation(translations, "process_library.browse_inside")} ${folder.name}`}
              onClick={handleOpenSubmenu}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                p: 0.5,
                ml: 0.5,
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <ArrowRight fontSize="small" />
            </Box>
          )}
        </Stack>
      </MenuItem>
      <Menu
        anchorEl={submenuAnchor}
        open={open}
        onClose={() => setSubmenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {(folder.children || []).map((child) => (
          <FolderNode
            key={child.id}
            folder={child}
            depth={0}
            excludeIds={excludeIds}
            currentParentId={currentParentId}
            onPick={handlePickFromSubmenu}
            onClose={onClose}
          />
        ))}
      </Menu>
    </React.Fragment>
  );
};

const MoveToFolderMenu: React.FC<MoveToFolderMenuProps> = ({
  anchorEl,
  onClose,
  folders,
  excludeIds,
  currentParentId,
  onPick,
}) => {
  const translations = useTranslations();
  const open = Boolean(anchorEl);

  const handlePick = (target: number | null) => {
    onPick(target);
    onClose();
  };

  // Top-level folder list comes from the root array. The page passes the
  // children of processModelFolders[0] (the actual root tree).
  const topLevel = useMemo(() => folders || [], [folders]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      MenuListProps={{ sx: { minWidth: 220, py: 0.5 } }}
    >
      <MenuItem
        onClick={() => handlePick(null)}
        selected={currentParentId === null}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Folder fontSize="small" sx={{ color: "primary.main" }} />
          <Typography variant="body2" fontWeight={600}>
            {getTranslation(translations, "process_library.root")}
          </Typography>
        </Stack>
      </MenuItem>
      {topLevel.map((folder) => (
        <FolderNode
          key={folder.id}
          folder={folder}
          depth={0}
          excludeIds={excludeIds}
          currentParentId={currentParentId}
          onPick={handlePick}
          onClose={onClose}
        />
      ))}
      {!topLevel.length && (
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {getTranslation(translations, "process_library.no_folders")}
          </Typography>
        </MenuItem>
      )}
    </Menu>
  );
};

export { collectDescendantIds };
export default MoveToFolderMenu;
